import { useMutation, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import React, { useCallback, useEffect, useState } from 'react';
import {
  CalendarType,
  ICalendar,
  IResource
} from '@gettactic/api';
import { Input, Tooltip } from '@/components/basic';
import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
import { classNames } from '@/lib/classNames';
import Select from 'react-select';
import { api } from '@/lib/api/api';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import {
  useCalendarByResource
} from '@gettactic/hooks';
import { useAuthenticated } from '@gettactic/hooks';
import { useAuthorizedIntegrations } from '@/hooks/useCalendar';

export interface CalendarsPanelProps {
  resource: IResource;
}

export type ConfirmOperationModal = {
  operation: 'refresh' | 'delete';
};

export const CalendarsPanel = ({ resource }: CalendarsPanelProps) => {
  const {
    userContext: { authenticatedUser, deviceTz }
  } = useAuthenticated();
  
  const [loading, setLoading] = useState(false);
  const { calendar, isLoading } = useCalendarByResource(resource.id);
  const [integrationIdInputValue, setIntegrationIdInputValue] =
    useState<string>(calendar?.integration_id ? calendar?.integration_id : '');
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedProviderLabel, setSelectedProviderLabel] =
    useState<string>('');

  const handleInputValueChange: React.ChangeEventHandler<HTMLInputElement> =
    useCallback((event) => {
      setIntegrationIdInputValue(event.target.value);
    }, []);

  const providerOptions = [
    { label: 'Microsoft Outlook', value: 'microsoft' },
    { label: 'Google Workspace', value: 'google' }
  ];

  const { authorizedIntegrations } = useAuthorizedIntegrations();
  const queryClient = useQueryClient();

  const hasCalendarIntegrations =
    authorizedIntegrations &&
    (authorizedIntegrations?.isMicrosoftAuthorized ||
      authorizedIntegrations?.isGoogleAuthorized);

  useEffect(() => {
    if (calendar?.integration_id) {
      setIntegrationIdInputValue(calendar?.integration_id);
    }
  }, [calendar]);

  useEffect(() => {
    setSelectedProvider(
      authorizedIntegrations?.isMicrosoftAuthorized &&
        authorizedIntegrations?.isGoogleAuthorized
        ? ''
        : !!authorizedIntegrations?.isMicrosoftAuthorized
          ? 'microsoft'
          : 'google'
    );
    setSelectedProviderLabel(
      authorizedIntegrations?.isMicrosoftAuthorized &&
        authorizedIntegrations?.isGoogleAuthorized
        ? ''
        : !!authorizedIntegrations?.isMicrosoftAuthorized
          ? 'Microsoft Outlook'
          : 'Google Workspace'
    );
  }, [authorizedIntegrations]);

  const saveCalendar = useMutation(
    async (calendar: ICalendar, slug = "") => {
      setLoading(true);
      return await api.client.calendar.createOrUpdate(
        calendar.organization_id,
        authenticatedUser.slug || "",
        calendar.source,
        calendar.type,
        calendar.reference_id,
        calendar.integration_id,
        calendar.is_enabled,
        calendar.is_pull_enabled,
        calendar.is_push_enabled,
        calendar.id
      );
    },
    {
      onSuccess: (r) => {
        toast.success('Changes saved');
      },
      onError: (e) => {
        toast.error(`Sorry we couldn't save the changes`);
      },
      onSettled: () => {
        queryClient.invalidateQueries(['integrations.calendar', resource.id]);
        setLoading(false);
      }
    }
  );

  const saveCalendarDetails = async () => {
    if (
      integrationIdInputValue === calendar?.integration_id ||
      !integrationIdInputValue
    ) {
      return;
    }

    if (!selectedProvider) {
      toast.error('Please select the Calendar Integration to use');
      return;
    }

    const calendarRequest = {
      organization_id: authenticatedUser.organization?.id,
      source: calendar?.source ?? selectedProvider,
      type: calendar?.type ?? CalendarType.Resource,
      reference_id: resource.id,
      integration_id: integrationIdInputValue,
      is_enabled: calendar?.integration_id ? calendar?.is_enabled : true,
      is_pull_enabled: calendar?.integration_id
        ? calendar?.is_pull_enabled
        : true,
      is_push_enabled: calendar?.integration_id
        ? calendar?.is_push_enabled
        : true,
      id: calendar?.integration_id ? calendar?.id : null
    } as ICalendar;

    saveCalendar.mutate(calendarRequest);
  };

  const onSelectedProviderChange = (value: any) => {
    setSelectedProvider(value.value);
    setSelectedProviderLabel(value.label);
  };

  return (
    <div>
      <div className="mt-4 flex items-center justify-between border-b border-gray-200 pb-2">
        <h3 className="flex items-center text-2xl font-medium leading-6 text-gray-900">
          Room Calendars
        </h3>
        <Tooltip>
          Adding a room calendar will sync all reservations to and from your
          third party calendar.
        </Tooltip>
      </div>
      <div>
        {!hasCalendarIntegrations ? (
          <div className="mt-5 flex flex-col w-full text-center">
            <span>Want to set up Calendar Integrations?</span>
            <br />
            <Link href="/settings/apps?activeTab=org-integrations">
              <a className="underline"> Visit Integrations page</a>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col w-full text-sm">
            {authorizedIntegrations?.isGoogleAuthorized &&
            authorizedIntegrations.isMicrosoftAuthorized ? (
              <div className="mt-5  w-full justify-between items-center">
                <h3 className="mt-4 text-base font-bold text-gray-800">
                  Calendar Integration
                </h3>
                <Select
                  onChange={onSelectedProviderChange}
                  className="w-full"
                  classNamePrefix="react-select"
                  placeholder="Select a calendar integration..."
                  options={providerOptions}
                />
              </div>
            ) : null}
            <div className="flex justify-between">
              <h3 className="mt-4 text-base font-bold text-gray-800">
                {`${selectedProviderLabel}`} Resource Email{' '}
              </h3>
              {calendar?.integration_id ? (
                <div
                  className={classNames(
                    calendar?.integration_id
                      ? 'text-green-600'
                      : 'text-gray-600',
                    'flex justify-end gap-1 mt-5'
                  )}
                >
                  {loading ? <LoadingSpinner /> : null}
                  <span>Connected</span>
                  <CheckCircle2Icon className="h-5 w-5 flex-none" />
                </div>
              ) : (
                <div
                  className={classNames(
                    calendar?.integration_id
                      ? 'text-green-600'
                      : 'text-gray-600',
                    'flex justify-end gap-1 mt-5'
                  )}
                >
                  {loading ? <LoadingSpinner /> : null}
                  <span>Not Connected</span>
                  <AlertCircleIcon className="h-5 w-5 flex-none" />
                </div>
              )}
            </div>
            <Input
              className="left-0 mt-4 w-full"
              value={integrationIdInputValue}
              onChange={handleInputValueChange}
              placeholder={`Type or select ${selectedProviderLabel} Resource Email`}
              disabled={loading}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  saveCalendarDetails();
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
